// src/components/VerifichePeriodicheTab.js
// Scadenzario delle verifiche periodiche obbligatorie (DPR 462/01 e normative correlate).
//
// Non è un semplice promemoria: è uno strumento di compliance. In caso di controllo o di
// contestazione, deve poter dimostrare che il monitoraggio era attivo — per questo ogni
// modifica alla data dell'ultima verifica, alla periodicità e allo stato finisce nel
// registro modifiche, e ogni verifica eseguita resta nello storico della scheda.
//
// Vista aggregata di tutte le scadenze di tutti gli impianti: le verifiche si guardano
// così, non impianto per impianto, altrimenti quella dimenticata non emerge mai.
import React, { useEffect, useState, useMemo } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useCanSee } from "../hooks/useCanSee";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { messaggioErrore } from "../utils/erroriUtente";
import { logModifica } from "../utils/logModifica";
import { esportaXLSX } from "../utils/esportaXLSX";
import { toLocalDateStr } from "../utils/dateUtils";
import { TIPOLOGIE_VERIFICA, STATI_VERIFICA, calcolaProssimaScadenza, giorniAllaScadenza,
         statoVerifica, testoScadenza, fmtDataIt } from "../utils/verificheUtils";
import { Plus, X, ShieldCheck, Search, Trash2, AlertTriangle, CheckCircle } from "lucide-react";

const inp = { border:"1px solid #e5e7eb", borderRadius:7, padding:"8px 10px", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%", boxSizing:"border-box" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

function formVuoto() {
  return { impiantoId:"", tipologiaVerifica:"", riferimentoNormativo:"", periodicitaMesi:"60",
    dataUltimaVerifica:"", enteEsecutore:"", statoDenuncia:"non_applicabile", protocolloDenuncia:"",
    responsabileUid:"", statoManuale:"", note:"" };
}

function SchedaModal({ scheda, impianti, utenti, orgId, currentUser, onClose }) {
  const { addToast } = useToast();
  const [form, setForm] = useState(() => scheda ? {...formVuoto(), ...scheda} : formVuoto());
  const [saving, setSaving] = useState(false);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));

  // Scegliendo una tipologia, periodicità e norma si precompilano — restano modificabili:
  // la periodicità dipende dall'ambiente reale, che solo chi conosce l'impianto sa valutare.
  const scegliTipologia = (nome) => {
    const t = TIPOLOGIE_VERIFICA.find(x=>x.nome===nome);
    setForm(f=>({ ...f, tipologiaVerifica:nome,
      periodicitaMesi: t ? String(t.mesi) : f.periodicitaMesi,
      riferimentoNormativo: t?.norma || f.riferimentoNormativo }));
  };

  const scadenzaCalcolata = calcolaProssimaScadenza({
    dataUltimaVerifica: form.dataUltimaVerifica, periodicitaMesi: Number(form.periodicitaMesi) });

  const salva = async () => {
    if (!form.impiantoId) { addToast("Seleziona l'impianto a cui si riferisce la verifica","error"); return; }
    if (!form.tipologiaVerifica.trim()) { addToast("La tipologia di verifica è obbligatoria","error"); return; }
    if (!(Number(form.periodicitaMesi) > 0)) { addToast("La periodicità dev'essere un numero di mesi maggiore di zero","error"); return; }
    if (saving) return;
    setSaving(true);
    try {
      const imp = impianti.find(i=>i.id===form.impiantoId);
      const dati = {
        ...form,
        periodicitaMesi: Number(form.periodicitaMesi),
        dataProssimaScadenza: scadenzaCalcolata || "",
        // Denominazione e cliente si salvano solo per leggibilità negli elenchi e negli
        // export: la fonte vera resta impiantoId.
        impiantoNome: imp?.denominazione || "",
        clienteNome: imp?.clienteNome || "",
        clienteId: imp?.clienteId || "",
        organizationId: orgId,
        updatedAt: serverTimestamp(),
      };
      if (scheda?.id) {
        await updateDoc(doc(db,"verifichePeriodiche",scheda.id), dati);
        // Tracciato in dettaglio ciò che conta per la difendibilità: data ultima verifica e
        // periodicità. Un controllo può contestare proprio quelle.
        if (scheda.dataUltimaVerifica !== dati.dataUltimaVerifica) {
          logModifica({ orgId, uid:currentUser.uid, nomeUtente:currentUser.displayName||"Utente",
            sezione:"verifichePeriodiche", refId:scheda.id, refNome:`${dati.impiantoNome} — ${dati.tipologiaVerifica}`,
            campo:"dataUltimaVerifica", campoLabel:"Data ultima verifica",
            before:scheda.dataUltimaVerifica||"(vuota)", after:dati.dataUltimaVerifica||"(vuota)" });
        }
        if (Number(scheda.periodicitaMesi) !== dati.periodicitaMesi) {
          logModifica({ orgId, uid:currentUser.uid, nomeUtente:currentUser.displayName||"Utente",
            sezione:"verifichePeriodiche", refId:scheda.id, refNome:`${dati.impiantoNome} — ${dati.tipologiaVerifica}`,
            campo:"periodicitaMesi", campoLabel:"Periodicità (mesi)",
            before:String(scheda.periodicitaMesi||""), after:String(dati.periodicitaMesi) });
        }
        addToast("Scheda aggiornata ✅","success");
      } else {
        const ref = await addDoc(collection(db,"verifichePeriodiche"), { ...dati, storico:[], createdAt: serverTimestamp(), createdBy: currentUser.uid });
        logModifica({ orgId, uid:currentUser.uid, nomeUtente:currentUser.displayName||"Utente",
          sezione:"verifichePeriodiche", refId:ref.id, refNome:`${dati.impiantoNome} — ${dati.tipologiaVerifica}`,
          campo:"scheda", campoLabel:"Scheda di verifica creata", before:"", after:dati.tipologiaVerifica });
        addToast("Scheda di verifica creata ✅","success");
      }
      onClose();
    } catch(err) { addToast(messaggioErrore(err),"error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:700, maxHeight:"90vh", overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700}}>🛡️ {scheda?.id ? "Modifica scheda di verifica" : "Nuova scheda di verifica"}</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>

        <div style={{marginBottom:12}}>
          <label style={lbl}>Impianto *</label>
          <select value={form.impiantoId} onChange={e=>upd("impiantoId",e.target.value)} style={inp}>
            <option value="">— Seleziona impianto —</option>
            {impianti.map(i => <option key={i.id} value={i.id}>{i.clienteNome ? `${i.clienteNome} — ` : ""}{i.denominazione}</option>)}
          </select>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Tipologia di verifica *</label>
            <input list="tipologie-verifica" value={form.tipologiaVerifica} onChange={e=>scegliTipologia(e.target.value)} style={inp}
              placeholder="Scegli o scrivi liberamente"/>
            <datalist id="tipologie-verifica">
              {TIPOLOGIE_VERIFICA.map(t=><option key={t.nome} value={t.nome}/>)}
            </datalist>
          </div>
          <div>
            <label style={lbl}>Periodicità (mesi) *</label>
            <input value={form.periodicitaMesi} onChange={e=>upd("periodicitaMesi",e.target.value)} style={inp} placeholder="Es. 24 o 60"/>
          </div>
        </div>

        <div style={{marginBottom:12}}>
          <label style={lbl}>Riferimento normativo</label>
          <input value={form.riferimentoNormativo} onChange={e=>upd("riferimentoNormativo",e.target.value)} style={inp}
            placeholder="Es. DPR 462/01 art. 4"/>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Data ultima verifica effettuata</label>
            <input type="date" value={form.dataUltimaVerifica} onChange={e=>upd("dataUltimaVerifica",e.target.value)} style={inp}/>
          </div>
          <div>
            <label style={lbl}>Ente / organismo che l'ha eseguita</label>
            <input value={form.enteEsecutore} onChange={e=>upd("enteEsecutore",e.target.value)} style={inp}
              placeholder="Es. ASL, organismo abilitato, interno"/>
          </div>
        </div>

        {form.dataUltimaVerifica && scadenzaCalcolata && (
          <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"9px 13px",marginBottom:12,fontSize:12.5,color:"#6d28d9"}}>
            💜 Prossima scadenza calcolata: <strong>{fmtDataIt(scadenzaCalcolata)}</strong> ({testoScadenza(scadenzaCalcolata)})
          </div>
        )}
        {!form.dataUltimaVerifica && (
          <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"9px 13px",marginBottom:12,fontSize:12,color:"#92400e"}}>
            ⚠️ Senza la data dell'ultima verifica non è possibile calcolare la scadenza. Se la verifica non è mai stata effettuata, la scheda resta censita ma senza scadenza: va programmata la prima verifica.
          </div>
        )}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Denuncia INAIL / ASL</label>
            <select value={form.statoDenuncia} onChange={e=>upd("statoDenuncia",e.target.value)} style={inp}>
              <option value="non_applicabile">Non applicabile</option>
              <option value="da_effettuare">Da effettuare</option>
              <option value="effettuata">Effettuata</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Protocollo / riferimento denuncia</label>
            <input value={form.protocolloDenuncia} onChange={e=>upd("protocolloDenuncia",e.target.value)} style={inp}/>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Responsabile interno</label>
            <select value={form.responsabileUid} onChange={e=>upd("responsabileUid",e.target.value)} style={inp}>
              <option value="">— Nessuno —</option>
              {utenti.map(u=><option key={u.uid} value={u.uid}>{u.displayName||u.email}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Stato lavorazione</label>
            <select value={form.statoManuale} onChange={e=>upd("statoManuale",e.target.value)} style={inp}>
              <option value="">Automatico (in base alla data)</option>
              <option value="in_pianificazione">In pianificazione</option>
              <option value="proposta_cliente">Proposta al cliente</option>
            </select>
          </div>
        </div>

        <div style={{marginBottom:16}}>
          <label style={lbl}>Note</label>
          <input value={form.note} onChange={e=>upd("note",e.target.value)} style={inp}/>
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={salva} disabled={saving}>{saving?"Salvo...":"Salva scheda"}</button>
        </div>
      </div>
    </div>
  );
}

// Registrazione di una verifica eseguita: sposta l'ultima verifica alla data indicata,
// ricalcola la scadenza successiva e archivia quella precedente nello storico — così la
// cronologia resta completa e dimostrabile, non sovrascritta.
function EseguitaModal({ scheda, orgId, currentUser, onClose }) {
  const { addToast } = useToast();
  const [data, setData] = useState(toLocalDateStr(new Date()));
  const [esito, setEsito] = useState("Favorevole");
  const [ente, setEnte] = useState(scheda.enteEsecutore || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const nuovaScadenza = calcolaProssimaScadenza({ dataUltimaVerifica:data, periodicitaMesi:scheda.periodicitaMesi });

  const conferma = async () => {
    if (!data) { addToast("Indica la data in cui è stata eseguita la verifica","error"); return; }
    if (saving) return;
    setSaving(true);
    try {
      const storico = [...(scheda.storico||[]), {
        dataVerifica: data, esito, ente, note,
        registrataDa: currentUser.displayName||"Utente",
        registrataIl: toLocalDateStr(new Date()),
      }];
      await updateDoc(doc(db,"verifichePeriodiche",scheda.id), {
        dataUltimaVerifica: data,
        dataProssimaScadenza: nuovaScadenza || "",
        enteEsecutore: ente,
        statoManuale: "", // si torna al calcolo automatico: la scadenza è nuova
        storico,
        updatedAt: serverTimestamp(),
      });
      logModifica({ orgId, uid:currentUser.uid, nomeUtente:currentUser.displayName||"Utente",
        sezione:"verifichePeriodiche", refId:scheda.id, refNome:`${scheda.impiantoNome} — ${scheda.tipologiaVerifica}`,
        campo:"verificaEseguita", campoLabel:`Verifica registrata come eseguita (esito: ${esito})`,
        before:scheda.dataUltimaVerifica||"(mai)", after:data });
      addToast(`Verifica registrata — prossima scadenza ${fmtDataIt(nuovaScadenza)} ✅`,"success");
      onClose();
    } catch(err) { addToast(messaggioErrore(err),"error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:520}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <h3 style={{fontSize:15,fontWeight:700}}>✅ Registra verifica eseguita</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <div style={{background:"#f9fafb",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12.5}}>
          <strong>{scheda.impiantoNome}</strong><br/>
          <span style={{color:"#6b7280"}}>{scheda.tipologiaVerifica} · ogni {scheda.periodicitaMesi} mesi</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={lbl}>Data di esecuzione *</label>
            <input type="date" value={data} onChange={e=>setData(e.target.value)} style={inp}/>
          </div>
          <div>
            <label style={lbl}>Esito</label>
            <select value={esito} onChange={e=>setEsito(e.target.value)} style={inp}>
              <option>Favorevole</option>
              <option>Favorevole con prescrizioni</option>
              <option>Negativo</option>
            </select>
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={lbl}>Ente / organismo esecutore</label>
          <input value={ente} onChange={e=>setEnte(e.target.value)} style={inp}/>
        </div>
        <div style={{marginBottom:14}}>
          <label style={lbl}>Note / prescrizioni</label>
          <input value={note} onChange={e=>setNote(e.target.value)} style={inp}/>
        </div>
        {nuovaScadenza && (
          <div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"9px 13px",marginBottom:14,fontSize:12.5,color:"#065f46"}}>
            La prossima scadenza sarà calcolata al <strong>{fmtDataIt(nuovaScadenza)}</strong>
          </div>
        )}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={conferma} disabled={saving}>{saving?"Registro...":"Registra"}</button>
        </div>
      </div>
    </div>
  );
}

export default function VerifichePeriodicheTab({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const canSee = useCanSee();
  const { addToast } = useToast();
  const [schede, setSchede] = useState([]);
  const [impianti, setImpianti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [cerca, setCerca] = useState("");
  const [filtroStato, setFiltroStato] = useState("tutte");
  const [modalTarget, setModalTarget] = useState(null);
  const [eseguitaTarget, setEseguitaTarget] = useState(null);
  const [storicoAperto, setStoricoAperto] = useState({});

  const canEdit = isOrgAdmin || isPlatformOwner || canSee("gestione-verifiche");

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"verifichePeriodiche"), where("organizationId","==",effectiveOrgId)),
      snap => setSchede(snap.docs.map(d=>({id:d.id,...d.data()}))));
  },[effectiveOrgId]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"impianti"), where("organizationId","==",effectiveOrgId)),
      snap => setImpianti(snap.docs.map(d=>({id:d.id,...d.data()})).filter(i=>i.statoImpianto!=="dismesso")));
  },[effectiveOrgId]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"users"), where("organizationId","==",effectiveOrgId)),
      snap => setUtenti(snap.docs.map(d=>({uid:d.id,...d.data()}))));
  },[effectiveOrgId]);

  const arricchite = useMemo(()=>schede.map(s=>{
    const scadenza = s.dataProssimaScadenza || calcolaProssimaScadenza(s);
    return { ...s, _scadenza: scadenza, _stato: statoVerifica(s), _giorni: giorniAllaScadenza(scadenza) };
  }),[schede]);

  const filtrate = useMemo(()=>{
    const q = cerca.trim().toLowerCase();
    return arricchite
      .filter(s => filtroStato==="tutte" || s._stato===filtroStato)
      .filter(s => !q || [s.impiantoNome,s.clienteNome,s.tipologiaVerifica,s.enteEsecutore]
        .some(v=>(v||"").toLowerCase().includes(q)))
      // Le scadute in cima, poi per data: quello che brucia si vede per primo
      .sort((a,b)=>{
        if (a._giorni===null) return 1;
        if (b._giorni===null) return -1;
        return a._giorni-b._giorni;
      });
  },[arricchite, cerca, filtroStato]);

  const conteggi = useMemo(()=>{
    const c = { scaduta:0, in_avviso:0, in_pianificazione:0, proposta_cliente:0, programmata:0 };
    arricchite.forEach(s=>{ if(c[s._stato]!==undefined) c[s._stato]++; });
    return c;
  },[arricchite]);

  const elimina = async (s) => {
    if (!await confirm(`Eliminare la scheda "${s.tipologiaVerifica}" di ${s.impiantoNome}? Verrà perso anche il suo storico.`, { type:"danger" })) return;
    try {
      await deleteDoc(doc(db,"verifichePeriodiche",s.id));
      logModifica({ orgId:effectiveOrgId, uid:currentUser.uid, nomeUtente:currentUser.displayName||"Utente",
        sezione:"verifichePeriodiche", refId:s.id, refNome:`${s.impiantoNome} — ${s.tipologiaVerifica}`,
        campo:"scheda", campoLabel:"Scheda di verifica eliminata", before:s.tipologiaVerifica, after:"" });
      addToast("Scheda eliminata","success");
    } catch(err) { addToast(messaggioErrore(err),"error"); }
  };

  const esporta = () => {
    if (filtrate.length===0) { addToast("Nessuna verifica da esportare con i filtri attuali","warning"); return; }
    const intestazione = ["Cliente","Impianto","Tipologia verifica","Riferimento normativo","Periodicità (mesi)",
      "Ultima verifica","Prossima scadenza","Giorni residui","Stato","Ente esecutore","Denuncia INAIL/ASL","Protocollo","Note"];
    const righe = filtrate.map(s=>[s.clienteNome||"—", s.impiantoNome||"—", s.tipologiaVerifica||"—",
      s.riferimentoNormativo||"—", s.periodicitaMesi||"—", fmtDataIt(s.dataUltimaVerifica),
      fmtDataIt(s._scadenza), s._giorni===null?"—":s._giorni, STATI_VERIFICA[s._stato]?.label||s._stato,
      s.enteEsecutore||"—",
      s.statoDenuncia==="effettuata"?"Effettuata":s.statoDenuncia==="da_effettuare"?"Da effettuare":"Non applicabile",
      s.protocolloDenuncia||"—", s.note||""]);
    esportaXLSX([intestazione,...righe], `Verifiche_Periodiche_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`, "Verifiche");
  };

  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:"#1e1b4b",display:"flex",alignItems:"center",gap:7}}>
            <ShieldCheck size={17} color="#7c3aed"/> Verifiche periodiche obbligatorie
          </div>
          <p style={{fontSize:11.5,color:"#6b7280",marginTop:2}}>
            Scadenze di legge sugli impianti dei clienti (DPR 462/01 e normative correlate) — ogni modifica resta tracciata.
          </p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={esporta}
            style={{padding:"7px 14px",border:"1px solid #059669",borderRadius:8,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            📊 Excel
          </button>
          {canEdit && (
            <button onClick={()=>setModalTarget({})} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:6}}>
              <Plus size={14}/> Nuova scheda
            </button>
          )}
        </div>
      </div>

      {impianti.length===0 && (
        <div style={{display:"flex",gap:8,background:"#fffbeb",border:"1px solid #fde68a",borderRadius:9,padding:"11px 14px",margin:"14px 0",fontSize:12.5,color:"#92400e"}}>
          <AlertTriangle size={15} style={{flexShrink:0,marginTop:1}}/>
          <div>Non ci sono ancora impianti censiti. Le verifiche si agganciano sempre a un impianto preciso —{" "}
            <button onClick={()=>onNavigate&&onNavigate({page:"impianti"})}
              style={{background:"none",border:"none",padding:0,color:"#7c3aed",fontWeight:700,cursor:"pointer",fontSize:12.5,textDecoration:"underline",fontFamily:"'Inter',sans-serif"}}>
              vai all'Anagrafica Impianti
            </button> per cominciare.
          </div>
        </div>
      )}

      {/* Riepilogo per stato: le scadute sempre in evidenza, come richiede la compliance */}
      <div style={{display:"flex",gap:8,margin:"16px 0",flexWrap:"wrap"}}>
        {[{k:"tutte",label:"Tutte",n:arricchite.length,col:"#6b7280",bg:"#f3f4f6"},
          {k:"scaduta",label:"Scadute",n:conteggi.scaduta,col:"#dc2626",bg:"#fef2f2"},
          {k:"in_avviso",label:"In avviso",n:conteggi.in_avviso,col:"#d97706",bg:"#fff7ed"},
          {k:"in_pianificazione",label:"In pianificazione",n:conteggi.in_pianificazione,col:"#2563eb",bg:"#eff6ff"},
          {k:"proposta_cliente",label:"Proposte al cliente",n:conteggi.proposta_cliente,col:"#7c3aed",bg:"#ede9fe"},
          {k:"programmata",label:"Programmate",n:conteggi.programmata,col:"#6b7280",bg:"#f9fafb"},
        ].map(b=>(
          <button key={b.k} onClick={()=>setFiltroStato(b.k)}
            style={{padding:"8px 14px",borderRadius:9,border:`1px solid ${filtroStato===b.k?b.col:"#e5e7eb"}`,
              background:filtroStato===b.k?b.bg:"#fff",cursor:"pointer",fontFamily:"'Inter',sans-serif",textAlign:"left"}}>
            <div style={{fontSize:17,fontWeight:800,color:b.col}}>{b.n}</div>
            <div style={{fontSize:11,color:"#6b7280"}}>{b.label}</div>
          </button>
        ))}
      </div>

      <div style={{position:"relative",marginBottom:14,maxWidth:400}}>
        <Search size={13} style={{position:"absolute",left:10,top:10,color:"#9ca3af"}}/>
        <input value={cerca} onChange={e=>setCerca(e.target.value)} placeholder="Cerca per impianto, cliente, tipologia..."
          style={{...inp, paddingLeft:30}}/>
      </div>

      {filtrate.length===0 ? (
        <div style={{textAlign:"center",padding:48,color:"#9ca3af",fontSize:13}}>
          {schede.length===0 ? "Nessuna scheda di verifica ancora censita." : "Nessuna verifica con questi filtri."}
        </div>
      ) : filtrate.map(s=>{
        const cfg = STATI_VERIFICA[s._stato] || STATI_VERIFICA.programmata;
        const resp = utenti.find(u=>u.uid===s.responsabileUid);
        const aperto = storicoAperto[s.id];
        return (
          <div key={s.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderLeft:`4px solid ${cfg.colore}`,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:240}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                  <span style={{fontSize:10,fontWeight:800,color:cfg.colore,background:cfg.bg,padding:"2px 8px",borderRadius:6}}>{cfg.label.toUpperCase()}</span>
                  <span style={{fontSize:13.5,fontWeight:700,color:"#111827"}}>{s.impiantoNome||"—"}</span>
                  {s.clienteNome && <span style={{fontSize:11.5,color:"#9ca3af"}}>· {s.clienteNome}</span>}
                </div>
                <div style={{fontSize:12.5,color:"#374151"}}>{s.tipologiaVerifica}</div>
                <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
                  {s.riferimentoNormativo && `${s.riferimentoNormativo} · `}
                  ogni {s.periodicitaMesi} mesi
                  {s.dataUltimaVerifica && ` · ultima ${fmtDataIt(s.dataUltimaVerifica)}`}
                  {resp && ` · resp. ${resp.displayName||resp.email}`}
                </div>
              </div>
              <div style={{textAlign:"right",minWidth:140}}>
                {s._scadenza ? (
                  <>
                    <div style={{fontSize:13,fontWeight:800,color:cfg.colore}}>{fmtDataIt(s._scadenza)}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>{testoScadenza(s._scadenza)}</div>
                  </>
                ) : (
                  <div style={{fontSize:11.5,color:"#d97706",fontWeight:600}}>Prima verifica<br/>da programmare</div>
                )}
              </div>
            </div>

            {s.statoDenuncia==="da_effettuare" && (
              <div style={{marginTop:8,fontSize:11.5,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:7,padding:"6px 10px"}}>
                ⚠️ Denuncia INAIL/ASL ancora da effettuare
              </div>
            )}
            {s.note && <div style={{marginTop:8,fontSize:11.5,color:"#6b7280"}}>📝 {s.note}</div>}

            <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap",alignItems:"center"}}>
              {canEdit && (
                <>
                  <button onClick={()=>setEseguitaTarget(s)}
                    style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"none",borderRadius:7,background:"#059669",color:"#fff",fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    <CheckCircle size={12}/> Registra verifica eseguita
                  </button>
                  <button onClick={()=>setModalTarget(s)}
                    style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:7,background:"#fff",color:"#374151",fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Modifica scheda
                  </button>
                </>
              )}
              {(s.storico||[]).length>0 && (
                <button onClick={()=>setStoricoAperto(o=>({...o,[s.id]:!o[s.id]}))}
                  style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:7,background:"#fff",color:"#6b7280",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  {aperto?"Nascondi":"Storico"} ({(s.storico||[]).length})
                </button>
              )}
              {canEdit && (
                <button onClick={()=>elimina(s)} title="Elimina scheda"
                  style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#dc2626",padding:2}}>
                  <Trash2 size={13}/>
                </button>
              )}
            </div>

            {aperto && (
              <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #f3f4f6"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:6}}>Storico verifiche eseguite</div>
                {[...(s.storico||[])].reverse().map((v,i)=>(
                  <div key={i} style={{fontSize:11.5,color:"#374151",padding:"5px 0",borderBottom:"1px solid #f9fafb"}}>
                    <strong>{fmtDataIt(v.dataVerifica)}</strong> — {v.esito}
                    {v.ente && ` · ${v.ente}`}
                    {v.note && <span style={{color:"#9ca3af"}}> · {v.note}</span>}
                    <span style={{color:"#d1d5db",fontSize:10.5}}> (registrata da {v.registrataDa})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {modalTarget && (
        <SchedaModal scheda={modalTarget.id?modalTarget:null} impianti={impianti} utenti={utenti}
          orgId={effectiveOrgId} currentUser={currentUser} onClose={()=>setModalTarget(null)}/>
      )}
      {eseguitaTarget && (
        <EseguitaModal scheda={eseguitaTarget} orgId={effectiveOrgId} currentUser={currentUser}
          onClose={()=>setEseguitaTarget(null)}/>
      )}
    </div>
  );
}
